"use client";
import { cn } from "@/lib/utils";
import { without } from "lodash-es";
import { ChevronRight } from "lucide-react";
import { Fragment, useCallback, useMemo, useState } from "react";

export type CategoryData = {
  id: number;
  name: string;
  children?: CategoryData[];
};

export type TreeData = {
  id: number;
  name: string;
  checked?: boolean | "half";
  expand?: boolean;
  children?: TreeData[];
}

export default function CategorySelector(props: {
  data?: CategoryData[];
  value?: number[];
  // eslint-disable-next-line no-unused-vars
  onChange?: (val: number[]) => void
}) {
  const [expand, setExpand] = useState<number[]>([]);
  const treeData: TreeData[] | undefined = 
    useMemo(() => props.data?.map((item) => {
      if (!item.children?.length) {
        return {
          ...item,
          checked: props.value?.includes(item.id),
        }
      }
      const halfChecked = item.children.find(
        (child) => props.value?.includes(child.id)
      )
      return {
        ...item,
        checked: halfChecked ? "half" : false,
        children: item.children.map((child) => ({
          ...child,
          checked: props.value?.includes(child.id),
        })),
        expand: expand.includes(item.id),
      }
    })
    , [props.data, props.value, expand]);

  const onExpand = useCallback((id: number) => {
    if (expand.includes(id)) {
      setExpand(without(expand, id));
    } else {
      setExpand([...expand, id]);
    }
  }, [expand]);

  const onChange = useCallback((id: number) => {
    if (props.value?.includes(id)) {
      props.onChange?.(without(props.value, id));
    } else {
      props.onChange?.([...(props.value || []), id])
    }
  }, [props]);

  return (
    <div className="w-full select-none">
      {treeData?.map((item, index) => (
        <Fragment key={item.id}>
          <div
            className={cn(
              "relative flex items-center cursor-pointer hover:text-primary gap-x-2 max-md:justify-between",
              {
                "pt-5 before:contents before:block before:h-5 before:border-l before:border-zinc-200 before:absolute before:top-0.5 before:left-[5.5px]": index !== 0,
                "before:border-dashed": index > 0 && treeData?.[index - 1]?.expand
              }
            )}
            onClick={() => {
              if (item.children?.length) {
                onExpand(item.id);
              } else {
                onChange(item.id);
              }
            }}
            title={item.name}
          >
            <span
              className={cn(
                "flex items-center text-sm",
                {
                  "text-base": item.children?.length,
                }
              )}
            >
              <span className={cn(
                "w-3 h-3 px-0.5 py-0.5 mr-2 rounded-md border border-black md:mr-3",
                {
                  "bg-black": item.checked === true,
                  "before:contents before:block before:w-full before:h-full before:bg-black before:rounded-[3px]": item.checked === "half",
                }
              )}></span>
              <span className="flex-auto overflow-hidden text-ellipsis whitespace-nowrap">{item.name}</span>
            </span>
            {!!item.children?.length && (
              <ChevronRight className={cn(
                "max-md:text-gray duration-300",
                {
                  "rotate-90": item.expand,
                }
              )} size={16} />
            )}
          </div>
          {!!item.children?.length && (
            <div
              className={cn(
                "w-full mt-1 pl-6 duration-300 overflow-hidden",
                {
                  "!max-h-0": !item.expand,
                }
              )}
              style={{
                maxHeight: 34 * item.children.length,
              }}
            >
              {item.children.map((child, i) => (
                <div
                  key={child.id}
                  className={cn(
                    "relative flex items-center text-sm cursor-pointer hover:text-primary",
                    {
                      "pt-3 before:contents before:block before:h-5 before:border-l before:border-zinc-200 before:absolute before:-top-1 before:left-[3.5px]": i !== 0,
                    }
                  )}
                  onClick={() => {
                    onChange(child.id);
                  }}
                >
                  <span className={cn(
                    "w-2 h-2 mr-1.5 text-sm rounded bg-zinc-200",
                    {
                      "bg-black": child.checked,
                    }
                  )}></span>
                  <span className="flex-auto whitespace-nowrap overflow-hidden text-ellipsis">{child.name}</span>
                </div>
              ))}
            </div>
          )}
        </Fragment>
      ))}
    </div>
  )
}